import { ActivateAccountComponent } from './pages/common/activate-account/activate-account.component';
import { SummaryComponent } from './pages/user/summary/summary.component';
import { QuizComponent } from './pages/user/quiz/quiz.component';
import { BreathExerciseComponent } from './pages/user/breath-exercise/breath-exercise.component';
import { EmailGuard } from './services/email-guard.service';
import { Role } from './models/role';
import { UserDeliveryAddressComponent } from './pages/user/delivery-address/delivery-address.component';
import { ReplaceBreathingDeviceComponent } from './pages/user/replace-breathing-device/replace-breathing-device.component';
import { BillingHistoryComponent } from './pages/user/billing-history/billing-history.component';
import { ManagerDashboardComponent } from './pages/user/manager-dashboard/manager-dashboard.component';
import { MarketingCollateralComponent } from './pages/user/marketing-collateral/marketing-collateral.component';
import { UserPaymentMethodComponent } from './pages/user/payment-method/payment-method.component';
import { SubscriptionComponent } from './pages/user/subscription/subscription.component';
import { ChangePasswordComponent } from './pages/user/change-password/change-password.component';
import { ChangeEmailComponent } from './pages/user/change-email/change-email.component';
import { EditDetailsComponent } from './pages/user/edit-details/edit-details.component';
import { ForgotPasswordComponent } from './pages/common/forgot-password/forgot-password.component';
import { DeleteUserComponent } from './pages/common/delete-user/delete-user.component';
import { NewPasswordComponent } from './pages/common/new-password/new-password.component';
import { ProfileComponent } from './pages/user/profile/profile.component';
import { DashboardComponent } from './pages/user/dashboard/dashboard.component';
import { PaymentMethodComponent } from './pages/common/payment-method/payment-method.component';
import { DeliveryAddressComponent } from './pages/common/delivery-address/delivery-address.component';
import { EmailConfirmationComponent } from './pages/common/email-confirmation/email-confirmation.component';
import { SignUpComponent } from './pages/common/sign-up/sign-up.component';
import { SignInComponent } from './pages/common/sign-in/sign-in.component';
import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { PortfolioComponent } from './pages/common/portfolio/portfolio.component';
import { AuthGuard } from './services/auth-guard.service';
import { VideosComponent } from './pages/user/videos/videos.component';
import { AdminModule } from './modules/admin/admin.module';
import { AdminGuard } from './services/admin-guard.service';
import { ListComponent as PodListComponent } from './pages/user/resources/pods/list/list.component';
import { ViewComponent as PodViewComponent } from './pages/user/resources/pods/view/view.component';
import { ListComponent as ScienceListComponent } from './pages/user/resources/science/list/list.component';
import { ViewComponent as ScienceViewComponent } from './pages/user/resources/science/view/view.component';
import { ListComponent as CommunityListComponent } from './pages/user/resources/community/list/list.component';
import { ViewComponent as CommunityViewComponent } from './pages/user/resources/community/view/view.component';
import { ListComponent as InsightListComponent } from './pages/user/resources/insights/list/list.component';
import { ViewComponent as InsightViewComponent } from './pages/user/resources/insights/view/view.component';
import { ListComponent as TipsListComponent } from './pages/user/tips/list/list.component';

const routes: Routes = [
  {
    path: '',
    component: SignInComponent,
    data: { title: 'Take a Breath' }
  },
  {
    path: 'sign-in',
    component: SignInComponent,
    data: { title: 'Sign In' }
  },
  {
    path: 'sign-up',
    component: SignUpComponent,
    data: { title: 'Sign Up' }
  },
  {
    path: 'delete-account',
    component: DeleteUserComponent,
    data: { title: 'Delete User Account' }
  },
  {
    path: 'activate/:email/:token',
    component: ActivateAccountComponent,
    data: { title: 'Activate Account' }
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent,
    data: { title: 'Forgot Password' }
  },
  {
    path: 'new-password/:email/:token',
    component: NewPasswordComponent,
    data: { title: 'New Password' }
  },
  {
    path: 'email-confirmation',
    component: EmailConfirmationComponent,
    data: { title: 'Email Confirmation' },
    canActivate: [EmailGuard]
  },
  {
    path: 'delivery-address',
    component: DeliveryAddressComponent,
    data: { title: 'Delivery Address' },
    canActivate: [EmailGuard]
  },
  {
    path: 'payment-method',
    component: PaymentMethodComponent,
    data: { title: 'Payment Method' },
    canActivate: [EmailGuard]
  },
  {
    path: 'user',
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: {
          title: 'Dashboard',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'profile',
        component: ProfileComponent,
        data: {
          title: 'Profile',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
       },
      {
        path: 'edit-details',
        component: EditDetailsComponent,
        data: {
          title: 'Edit Details',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'change-email',
        component: ChangeEmailComponent,
        data: {
          title: 'Change Email',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'change-password',
        component: ChangePasswordComponent,
        data: {
          title: 'Change Password',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'subscription',
        component: SubscriptionComponent,
        data: {
          title: 'Change Subscription',
          roles: [Role.Individual]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'payment-method',
        component: UserPaymentMethodComponent,
        data: {
          title: 'Change Payment Method',
          roles: [Role.Individual]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'billing-history',
        component: BillingHistoryComponent,
        data: {
          title: 'Billing History',
          roles: [Role.Individual]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'manager-dashboard',
        component: ManagerDashboardComponent,
        data: {
          title: 'Manager Dashboard',
          roles: [Role.Manager]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'marketing-collateral',
        component: MarketingCollateralComponent,
        data: {
          title: 'Marketing Collateral',
          roles: [Role.Manager]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'replace-breathing-device',
        component: ReplaceBreathingDeviceComponent,
        data: {
          title: 'Replace Breathing Device',
          roles: [Role.Individual]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'delivery-address',
        component: UserDeliveryAddressComponent,
        data: {
          title: 'Change Delivery Address',
          roles: [Role.Individual]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'videos/:seriesId/:moduleId',
        component: VideosComponent,
        data: {
          title: 'Videos',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'breathing-exerise/:seriesId/:moduleId',
        component: BreathExerciseComponent,
        data: {
          title: 'Breathing Exercise',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'quiz/:seriesId/:moduleId',
        component: QuizComponent,
        data: {
          title: 'Quiz',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'summary/:seriesId/:moduleId',
        component: SummaryComponent,
        data: {
          title: 'Summary',
          roles: [Role.Individual, Role.Employee, Role.Manager, Role.Admin]
        },
        canActivate: [AuthGuard]
      },
      {
        path: 'resources',
        children: [
          {
            path: 'pods',
            component: PodListComponent,
            data: { title: 'Audio Pods' }
          },
          {
            path: 'pods/:id',
            component: PodViewComponent,
            data: { title: 'Audio Pods' }
          },
          {
            path: 'community',
            component: CommunityListComponent,
            data: { title: 'Community' }
          },
          {
            path: 'community/:id',
            component: CommunityViewComponent,
            data: { title: 'Community' }
          },
          {
            path: 'science',
            component: ScienceListComponent,
            data: { title: 'Science' }
          },
          {
            path: 'science/:id',
            component: ScienceViewComponent,
            data: { title: 'Science' }
          },
          {
            path: 'insights',
            component: InsightListComponent,
            data: { title: 'Insights' }
          },
          {
            path: 'insights/:id',
            component: InsightViewComponent,
            data: { title: 'Insights' }
          },
        ],
      },
      {
        path: 'tips',
        component: TipsListComponent,
        data: { title: 'Tips' }
      },
    ],
  },
  {
    path : 'admin',
    loadChildren : './modules/admin/admin.module#AdminModule',
    data: { roles: [Role.Admin] },
    canActivate: [AdminGuard],
  },

  // Redirect to sign-in page if route not found
  { path: '**', redirectTo: '' }
];

@NgModule({
  imports:
  [
    RouterModule.forRoot(routes, {
      anchorScrolling: 'enabled',
      onSameUrlNavigation: 'reload'
    }),
    AdminModule
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
